/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (9 URL testées, révisé après re-vérification). Daté au moment des faits : 2025-12-08.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "nuit-de-linfo-2025-besancon-85-etudiants",
  "title": "Nuit de l'Info 2025 : 85 étudiants inscrits à Besançon pour coder jusqu'au matin",
  "excerpt": "Dans la nuit du 4 au 5 décembre, seize équipes inscrites à Besançon devaient livrer une application web au petit matin, sur un sujet national consacré à la dépendance numérique des établissements.",
  "seoTitle": "Nuit de l'Info 2025 : 85 étudiants inscrits",
  "seoDescription": "Seize équipes et 85 participants inscrits à l'université pour coder une application web en une nuit, du 4 décembre 16h34 au 5 décembre 8h04. Récit et défis.",
  "category": "formation",
  "tags": [
    "Nuit de l'Info",
    "OFNI",
    "Université Marie et Louis Pasteur",
    "FEMTO-ST",
    "Hackathon"
  ],
  "author": "jimmy",
  "publishedAt": "2025-12-08",
  "updatedAt": "2025-12-08",
  "featured": false,
  "cover": {
    "image": "/img/blog/junction-2015.jpg",
    "credit": "Photo : Junction 2015 (hackathon) — Vmuru, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Grande salle de hackathon éclairée en bleu, étudiants concentrés devant des ordinateurs portables et des lampes de bureau"
  },
  "content": [
    {
      "type": "lead",
      "text": "Jeudi 4 décembre, à 16h34 — heure nationale de démarrage [^4] —, la Nuit de l'Info s'est ouverte simultanément sur l'ensemble de ses sites d'accueil. Le planning officiel cale le début de la nuit sur le coucher du soleil à Mulhouse et sa fin sur le lever du soleil au même endroit [^1]. À Besançon, seize équipes et quatre-vingt-cinq participants étaient inscrits [^2]. Quinze heures et demie plus tard, à 8h04 le vendredi matin, chaque équipe devait avoir livré une application web."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "La Nuit de l'Info, compétition nationale de développement web en une nuit"
        },
        {
          "label": "Quand",
          "value": "Du jeudi 4 décembre 2025 à 16h34 au vendredi 5 décembre à 8h04, horaire national"
        },
        {
          "label": "Où",
          "value": "Site d'accueil de Besançon, à l'université ; lieu précis non communiqué"
        },
        {
          "label": "Qui",
          "value": "16 équipes et 85 participants inscrits, en groupes de 2 à 11 membres"
        },
        {
          "label": "Pour qui",
          "value": "Étudiants en informatique et profils associés, du design à la communication"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une nuit, une application, cinq défis maximum"
    },
    {
      "type": "p",
      "text": "Le principe de la Nuit de l'Info tient en une phrase : « développer une application informatique en une nuit ». Les équipes travaillent en simultané sur l'ensemble des sites du pays, selon un horaire commun calé sur le coucher puis le lever du soleil, et le cahier des charges n'est dévoilé qu'au démarrage [^1]. Le format n'est pas réservé aux développeurs : l'organisation cite parmi les compétences utiles la programmation, mais aussi le design, la communication ou les réseaux sociaux, et ne fixe qu'un plancher de deux personnes par équipe [^5]."
    },
    {
      "type": "p",
      "text": "À cette contrainte de temps s'ajoute une mécanique de *défis*, proposés par des entreprises, des institutions publiques ou des associations, chacun doté de son propre prix [^5]. Quarante-deux défis étaient déposés pour l'édition 2025 — éco-conception, cybersécurité, ergonomie, place des femmes en informatique [^7] —, et chaque équipe ne pouvait en retenir que cinq au maximum, à articuler avec le sujet national [^1]. Là se joue une bonne part de la stratégie : viser large, ou concentrer ses forces sur un livrable défendable au petit matin."
    },
    {
      "type": "ul",
      "items": [
        "**Début** : jeudi 4 décembre 2025 à 16h34, avec la révélation du sujet national [^4]",
        "**Fin** : vendredi 5 décembre à 8h04, lever du soleil à Mulhouse [^1]",
        "**Défis** : cinq au maximum par équipe, en plus du sujet national [^1]",
        "**Équipes** : deux personnes au minimum, profils techniques et non techniques [^5]",
        "**Évaluation** : jurys réunis le vendredi matin [^1]",
        "**Résultats** : diffusés à partir du lendemain midi de la nuit, et sous une semaine [^1]"
      ]
    },
    {
      "type": "h2",
      "text": "Le sujet 2025 : desserrer la dépendance numérique des établissements"
    },
    {
      "type": "p",
      "text": "Le sujet, tenu secret jusqu'à l'ouverture, a été formulé en ces termes : créer une application montrant « de façon ludique, vivante, engageante comment un établissement peut réduire sa dépendance numérique et rejoindre l'aventure nird » [^4]. NIRD, pour « Numérique Inclusif Responsable Durable », est une démarche lancée par un collectif d'enseignants qui promeut le logiciel libre, les systèmes légers et le matériel reconditionné dans les établissements scolaires [^9]. Autrement dit, une commande d'intérêt public plutôt qu'un exercice technique gratuit : rendre lisible, pour des élèves, des enseignants ou une collectivité, ce que recouvre concrètement ce choix d'outils."
    },
    {
      "type": "p",
      "text": "Il place aussi les étudiants dans une position inconfortable et instructive : défendre la sobriété et l'autonomie numériques en une nuit, avec les outils, les hébergements et les bibliothèques qu'ils ont sous la main."
    },
    {
      "type": "h2",
      "text": "À Besançon : seize équipes inscrites à l'université"
    },
    {
      "type": "p",
      "text": "Le [site d'accueil bisontin](https://www.nuitdelinfo.com/inscription/sites/56) affichait, à la clôture des inscriptions, seize équipes et quatre-vingt-cinq participants enregistrés [^2]. Les effectifs vont de deux à onze membres selon les équipes, signe de stratégies différentes : spécialisation d'un côté, masse critique pour couvrir plusieurs défis de l'autre. Le seul contact publié pour le site est une adresse électronique à [l'institut de recherche FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques) [^2]."
    },
    {
      "type": "p",
      "text": "L'OFNI, l'association des étudiants en informatique de l'université, cite la Nuit de l'Info parmi les activités qu'elle propose [^3] ; la page d'inscription du site bisontin, elle, ne nomme aucune association organisatrice. Créée en 1997, l'association réunit les promotions autour d'activités communes et se présente comme un intermédiaire entre ses membres, les chercheurs et les entreprises [^3]. À noter : ces deux pages parlent encore d'« Université de Franche-Comté » [^2] [^3], alors que l'établissement porte le nom d'[Université Marie et Louis Pasteur](/blog/se-former-ia-besancon-guide-voies-acces) depuis le 1er janvier 2025 [^8]."
    },
    {
      "type": "p",
      "text": "À l'échelle nationale, l'ordre de grandeur reste celui d'un événement de masse : l'édition 2024 avait rassemblé 6 027 participants répartis en 904 équipes sur 72 sites [^1], soit une moyenne d'environ quatre-vingts participants et douze équipes par site. Avec seize équipes et quatre-vingt-cinq inscrits [^2], Besançon se situe dans cet ordre de grandeur. L'IUT de Paris - Rives de Seine, lui, annonce onze équipes et cent douze étudiants pour l'édition 2025 [^6]."
    },
    {
      "type": "h2",
      "text": "L'IA générative, invitée discrète"
    },
    {
      "type": "p",
      "text": "Reste une question ouverte : la place des assistants de code dans un tel exercice. Le sujet 2025 ne porte pas sur l'intelligence artificielle mais sur la dépendance numérique des établissements [^4] — un périmètre où les outils d'IA générative, hébergés par de grands acteurs du numérique, s'invitent naturellement. Les sources publiques consultées ne détaillent ni les défis retenus par les équipes bisontines, ni d'éventuelles règles d'encadrement de ces outils. Nous n'irons donc pas plus loin que le constat."
    },
    {
      "type": "callout",
      "tone": "info",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Un site d'accueil local, ce n'est pas qu'une salle ouverte la nuit : c'est un point de contact entre des étudiants, un laboratoire de recherche et les organisations — entreprises, institutions publiques, associations — qui portent et dotent les défis [^5]. Pour une ville dont l'écosystème numérique reste de taille modeste, ces quinze heures et demie fonctionnent comme un sas : elles donnent à voir, très tôt dans un cursus, ce que valent le travail en équipe et la tenue d'un délai."
    },
    {
      "type": "h2",
      "text": "Ce qu'il reste à savoir"
    },
    {
      "type": "p",
      "text": "La diffusion des résultats commence dès le lendemain de la nuit et s'étale sur la semaine qui suit [^1] : les sources publiques consultées ne permettent pas encore de dire si des équipes bisontines figurent au palmarès des défis partenaires. L'essentiel est peut-être ailleurs : quatre-vingt-cinq inscriptions sur un site universitaire de province, pour une nuit passée à coder sur un sujet d'intérêt collectif, disent quelque chose de la vitalité d'une filière que l'on regarde trop souvent par le seul prisme de l'emploi."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "La Nuit de l'Info — site officiel",
      "publisher": "Nuit de l'Info",
      "url": "https://www.nuitdelinfo.com/",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "Site d'accueil de Besançon — inscriptions et équipes",
      "publisher": "Nuit de l'Info",
      "url": "https://www.nuitdelinfo.com/inscription/sites/56",
      "type": "official",
      "date": ""
    },
    {
      "id": 3,
      "title": "Présentation de l'association OFNI",
      "publisher": "OFNI — association des étudiants en informatique de Besançon",
      "url": "https://ofni.asso.fr/presentation",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Nuit de l'Info 2025, un marathon numérique étudiant",
      "publisher": "Université d'Angers — Faculté des sciences",
      "url": "https://www.univ-angers.fr/fr/acces-directs/facultes-et-instituts/faculte-des-sciences/actualites/actu/nuit-de-l-info-2025.html",
      "type": "academic",
      "date": ""
    },
    {
      "id": 5,
      "title": "Foire aux questions de la Nuit de l'Info",
      "publisher": "Nuit de l'Info",
      "url": "https://www.nuitdelinfo.com/faq/",
      "type": "official",
      "date": ""
    },
    {
      "id": 6,
      "title": "La Nuit de l'Info 2025 : 11 équipes, une nuit, des idées à foison",
      "publisher": "IUT de Paris - Rives de Seine, Université Paris Cité",
      "url": "https://iutparis-seine.u-paris.fr/la-nuit-de-linfo-2025-11-equipes-une-nuit-des-idees-a-foison/",
      "type": "academic",
      "date": "2025-12-06"
    },
    {
      "id": 7,
      "title": "Liste des défis déposés — édition 2025",
      "publisher": "Nuit de l'Info",
      "url": "https://www.nuitdelinfo.com/inscription/defis/liste",
      "type": "official",
      "date": ""
    },
    {
      "id": 8,
      "title": "Ne l'appelez plus université de Franche-Comté, l'université Marie et Louis Pasteur est née",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/ne-lappelez-plus-universite-de-franche-comte-luniversite-marie-et-louis-pasteur-est-nee/",
      "type": "press",
      "date": "2025-01-14"
    },
    {
      "id": 9,
      "title": "Le CNLL soutient la démarche NIRD pour un numérique libre et durable dans les établissements scolaires",
      "publisher": "CNLL",
      "url": "https://cnll.fr/news/le-cnll-soutient-la-d%C3%A9marche-nird-pour-un-num%C3%A9rique-libre-et-durable-dans-les-%C3%A9tablissements-scolaires/",
      "type": "official",
      "date": "2025-11-21"
    }
  ]
};
